/**
 * The Resolution tab — Step 127, surfaced by Step 45.
 *
 * From `[#5804]`: **"Add Resolution"** and — the good bit — **"Closed (on
 * time)"**, the SLA outcome stated ON the ticket at the moment of closure
 * rather than only in a report.
 *
 * **Three labelled sections, not one box.** The reference asks three
 * different questions — why it broke, what we did, what the requester is told
 * — and collapsed into a single field, the first two are the ones that stop
 * being written. That is the whole difference between a resolution note and a
 * knowledge base you can actually build from later.
 *
 * DRAFT-FIRST, absolutely: this is free-form text, so nothing commits until
 * Save and `useDirty` owns the Keep/Discard guard. The auto-commit exception
 * is for single-select properties only.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';

interface Resolution {
  rootCause: string | null;
  actionsTaken: string | null;
  resolution: string | null;
  slaVerdict: 'met' | 'breached' | 'none' | null;
  resolvedAt: number | null;
  updatedAt: number;
}

const SECTIONS = [
  {
    key: 'rootCause' as const,
    label: 'Root cause',
    hint: 'Why it happened. The part a problem record is built from later.',
  },
  { key: 'actionsTaken' as const, label: 'Actions taken', hint: 'What was actually done, in order.' },
  {
    key: 'resolution' as const,
    label: 'Resolution',
    hint: 'What the requester is told. This is the outward-facing one.',
  },
];

/** The verdict, stated plainly. `none` is its own answer — a ticket with no
 *  policy had no promise to keep, and calling that "on time" would flatter
 *  the numbers. */
const VERDICT = {
  met: { label: 'Closed on time', icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400' },
  breached: { label: 'Closed late', icon: AlertTriangle, cls: 'text-destructive' },
  none: { label: 'No SLA applied', icon: MinusCircle, cls: 'text-muted-foreground' },
};

export function ResolutionTab({
  id,
  ticketType,
  canEdit,
  onSaved,
}: {
  id: string;
  /**
   * Step 174 — what is being resolved decides what it is sensible to ask.
   *
   * Kai: *"service requests require resolution notes, root cause etc and they
   * shouldn't."* A service request has no root cause. Nothing went wrong;
   * somebody wanted a laptop. Asking is not merely redundant, it teaches
   * people that the fields here are boxes to fill rather than a record worth
   * keeping — which costs you the incidents where they matter.
   */
  ticketType?: string;
  canEdit: boolean;
  /** Called after a successful save. The status-change prompt uses it to
   *  retry the change the agent already asked for, rather than making them
   *  press Update a second time. */
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.tickets.resolution(id),
    queryFn: () => apiFetch<{ entry: Resolution | null }>(`/api/tickets/${id}/resolution`),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  /**
   * What the server last agreed to — the thing `draft` is dirty AGAINST.
   *
   * Step 192. This used to compare against the live query cache, re-armed by
   * a `setLoaded(false)` in the mutation's onSuccess. `invalidateQueries` does
   * not refetch synchronously, so that re-arm fired one render later against
   * the still-cached PRE-save row and seeded the form from it: every field
   * blanked the instant you saved. Saving again then wrote the blanks back.
   * A baseline cannot race — it moves only when we say so.
   */
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || loaded) return;
    const e = q.data.entry;
    const initial = {
      rootCause: e?.rootCause ?? '',
      actionsTaken: e?.actionsTaken ?? '',
      resolution: e?.resolution ?? '',
    };
    setDraft(initial);
    setBaseline(initial);
    setLoaded(true);
  }, [q.data, loaded]);

  /** Root cause is an INCIDENT question. A request was fulfilled, not fixed. */
  const sections = SECTIONS.filter(
    (sec) => sec.key !== 'rootCause' || (ticketType !== 'service_request' && ticketType !== 'task'),
  );
  const optional = ticketType === 'service_request';

  const stored = q.data?.entry;
  const dirty =
    loaded &&
    (draft.rootCause !== baseline.rootCause ||
      draft.actionsTaken !== baseline.actionsTaken ||
      draft.resolution !== baseline.resolution);
  const clearDirty = useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend(`/api/tickets/${id}/resolution`, 'PUT', draft),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save the resolution.');
      // The draft we just sent IS the new baseline. The verdict below reads
      // the refetched row, so it still appears without re-seeding the form.
      setBaseline(draft);
      clearDirty();
      toast.success('Resolution saved.');
      void qc.invalidateQueries({ queryKey: qk.tickets.resolution(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
      onSaved?.();
    },
  });

  if (q.isPending)
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );

  const v = stored?.slaVerdict ? VERDICT[stored.slaVerdict] : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {v && (
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <v.icon className={cn('h-4 w-4 shrink-0', v.cls)} />
            <span className={cn('text-sm font-medium', v.cls)}>{v.label}</span>
            {stored?.resolvedAt && (
              <span className="text-xs text-muted-foreground">· recorded {fullTime(stored.resolvedAt)}</span>
            )}
          </div>
        )}
        {!stored && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            {optional
              ? 'No resolution recorded yet. Optional for a request — add one if there is anything worth saying.'
              : 'No resolution recorded yet. One is required before this ticket can be resolved or closed.'}
          </p>
        )}

        {sections.map((s) => (
          <div key={s.key}>
            <label className="mb-1 block text-sm font-semibold">{s.label}</label>
            <p className="mb-1.5 text-xs text-muted-foreground">{s.hint}</p>
            {canEdit ? (
              <RichTextEditor
                value={draft[s.key] ?? ''}
                onChange={(html) => setDraft((d) => ({ ...d, [s.key]: html }))}
              />
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded-md border p-3"
                // Sanitised on WRITE, never on render — the standing rule.
                dangerouslySetInnerHTML={{ __html: draft[s.key] || '<p class="text-sm">—</p>' }}
              />
            )}
          </div>
        ))}
      </div>
      {/* Docked to the bottom of the viewport, like the properties rail's
          Update (Kai). The body above scrolls; this does not, so on a long
          write-up Save is never a scroll away. */}
      {canEdit && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-card px-4 py-2.5">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save resolution'}
          </Button>
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}
